import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users, Ship, Calendar, AlertTriangle, TrendingUp, Clock,
  CheckCircle, XCircle, ArrowRight, Anchor, UserCheck, AlertCircle,
  BarChart3, Activity, Globe, Target, Briefcase, Timer
} from 'lucide-react';
import { crewApi } from '../services/api';
import type { DashboardOverview, Alert, WorkflowStats, VesselCoverage, RankDistribution } from '../types';

// Stat Card Component
const StatCard = ({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  color = 'blue',
  trend,
  onClick
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ElementType;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'cyan';
  trend?: { value: number; label: string };
  onClick?: () => void;
}) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    yellow: 'bg-amber-50 text-amber-600 border-amber-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-all ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend.value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            <TrendingUp className={`w-4 h-4 mr-1 ${trend.value < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(trend.value)}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
        <p className="text-sm font-medium text-gray-600 mt-1">{title}</p>
        {subtitle && (
          <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
        )}
      </div>
      {onClick && (
        <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
          View Details <ArrowRight className="w-4 h-4 ml-1" />
        </div>
      )}
    </motion.div>
  );
};

// Alert Item Component
const AlertItem = ({ alert, onClick }: { alert: Alert; onClick?: () => void }) => {
  const severityColors = {
    high: 'border-l-red-500 bg-red-50',
    medium: 'border-l-amber-500 bg-amber-50',
    low: 'border-l-blue-500 bg-blue-50',
  };

  const severityIcons = {
    high: <AlertTriangle className="w-5 h-5 text-red-500" />,
    medium: <AlertCircle className="w-5 h-5 text-amber-500" />,
    low: <Clock className="w-5 h-5 text-blue-500" />,
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`p-4 border-l-4 rounded-r-lg ${severityColors[alert.severity]} cursor-pointer hover:shadow-md transition-all`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {severityIcons[alert.severity]}
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900">{alert.title}</h4>
          <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
          <p className="text-xs text-gray-400 mt-2">
            {new Date(alert.created_at).toLocaleString()}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Progress Ring Component
const ProgressRing = ({ progress, size = 120, strokeWidth = 10, color = 'blue' }: {
  progress: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-gray-200"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className={`text-${color}-500`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-900">{Math.round(progress)}%</span>
      </div>
    </div>
  );
};

// Rank Bar Component
const RankBar = ({ rank, active, available, maxValue }: RankDistribution & { maxValue: number }) => {
  const activePercent = (active / maxValue) * 100;
  const availablePercent = (available / maxValue) * 100;

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">{rank}</span>
        <span className="text-sm text-gray-500">{active + available} total</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
        <div 
          className="h-full bg-blue-500 transition-all duration-500" 
          style={{ width: `${activePercent}%` }}
        />
        <div 
          className="h-full bg-emerald-500 transition-all duration-500" 
          style={{ width: `${availablePercent}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-500">
        <span className="flex items-center">
          <span className="w-2 h-2 bg-blue-500 rounded-full mr-1" />
          Active: {active}
        </span>
        <span className="flex items-center">
          <span className="w-2 h-2 bg-emerald-500 rounded-full mr-1" />
          Available: {available}
        </span>
      </div>
    </div>
  );
};

// Main Dashboard Component
export default function DashboardPage() {
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState<DashboardOverview | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [workflowStats, setWorkflowStats] = useState<WorkflowStats | null>(null);
  const [vesselCoverage, setVesselCoverage] = useState<VesselCoverage | null>(null);
  const [rankDistribution, setRankDistribution] = useState<RankDistribution[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const [stats, alertsData, workflow, coverage, ranks] = await Promise.all([
        crewApi.getDashboardStats(),
        crewApi.getAlerts(),
        crewApi.getWorkflowStats(),
        crewApi.getVesselCoverage(),
        crewApi.getCrewByRank(),
      ]);
      
      setDashboardStats(stats);
      setAlerts(alertsData);
      setWorkflowStats(workflow);
      setVesselCoverage(coverage);
      setRankDistribution(ranks);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const maxRankValue = rankDistribution.reduce((max, r) => Math.max(max, r.active + r.available), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Operations Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Real-time overview of crew management operations • Last updated: {new Date().toLocaleString()}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/assignments/new')}
            className="flex items-center gap-3 p-4 bg-blue-600 text-white rounded-xl shadow-lg hover:bg-blue-700 transition-colors"
          >
            <UserCheck className="w-6 h-6" />
            <span className="font-medium">New Assignment</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/relief-plans')}
            className="flex items-center gap-3 p-4 bg-white border border-gray-200 text-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all"
          >
            <Calendar className="w-6 h-6" />
            <span className="font-medium">Relief Plans</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-3 p-4 bg-white border border-gray-200 text-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all"
          >
            <Target className="w-6 h-6" />
            <span className="font-medium">AI Search</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/vessels')}
            className="flex items-center gap-3 p-4 bg-white border border-gray-200 text-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all"
          >
            <Ship className="w-6 h-6" />
            <span className="font-medium">Manage Vessels</span>
          </motion.button>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Active Crew"
            value={dashboardStats?.overview.total_active_crew || 0}
            subtitle="Currently on vessels"
            icon={Users}
            color="blue"
            onClick={() => navigate('/active-crew')}
          />
          <StatCard
            title="Available Crew"
            value={dashboardStats?.overview.total_available_crew || 0}
            subtitle="Ready for assignment"
            icon={UserCheck}
            color="green"
            onClick={() => navigate('/available')}
          />
          <StatCard
            title="Total Vessels"
            value={dashboardStats?.overview.total_vessels || 0}
            subtitle="In fleet"
            icon={Ship}
            color="cyan"
            onClick={() => navigate('/vessels')}
          />
          <StatCard
            title="Relief Plans"
            value={dashboardStats?.overview.total_relief_plans || 0}
            subtitle={`${dashboardStats?.relief_status.pending || 0} pending`}
            icon={Calendar}
            color="purple"
            onClick={() => navigate('/relief-plans')}
          />
        </div>

        {/* Relief Status & Workflow */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Relief Status */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
              Relief Plan Status
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <div className="text-3xl font-bold text-amber-600">
                  {dashboardStats?.relief_status.pending || 0}
                </div>
                <div className="text-sm text-amber-700 mt-1">Pending</div>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <div className="text-3xl font-bold text-emerald-600">
                  {dashboardStats?.relief_status.confirmed || 0}
                </div>
                <div className="text-sm text-emerald-700 mt-1">Confirmed</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-3xl font-bold text-red-600">
                  {dashboardStats?.relief_status.overdue || 0}
                </div>
                <div className="text-sm text-red-700 mt-1">Overdue</div>
              </div>
            </div>
          </div>

          {/* Workflow Completion */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-blue-600" />
              Assignment Workflow
            </h3>
            <div className="flex items-center justify-center">
              <ProgressRing 
                progress={workflowStats?.completion_rate || 0} 
                color="blue"
              />
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                {workflowStats?.total_confirmed || 0} of {(workflowStats?.total_confirmed || 0) + (workflowStats?.total_pending || 0)} assignments completed
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {workflowStats?.total_unassigned || 0} positions need assignment
              </p>
            </div>
          </div>

          {/* Vessel Coverage */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Anchor className="w-5 h-5 mr-2 text-blue-600" />
              Fleet Coverage
            </h3>
            <div className="flex items-center justify-center">
              <ProgressRing 
                progress={vesselCoverage?.coverage_percentage || 0} 
                color="emerald"
              />
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                {vesselCoverage?.vessels_with_active_crew || 0} of {vesselCoverage?.total_vessels || 0} vessels have active crew
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {vesselCoverage?.vessels_with_relief_plans || 0} vessels with relief plans
              </p>
            </div>
          </div>
        </div>

        {/* Upcoming Due Dates & Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Upcoming Due Dates */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Timer className="w-5 h-5 mr-2 text-blue-600" />
              Upcoming Sign-offs
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-red-500" />
                  <span className="font-medium text-gray-900">Next 7 Days</span>
                </div>
                <span className="text-2xl font-bold text-red-600">
                  {dashboardStats?.upcoming_due_dates.next_7_days || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span className="font-medium text-gray-900">Next 14 Days</span>
                </div>
                <span className="text-2xl font-bold text-amber-600">
                  {dashboardStats?.upcoming_due_dates.next_14_days || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <span className="font-medium text-gray-900">Next 30 Days</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">
                  {dashboardStats?.upcoming_due_dates.next_30_days || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Alerts */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-amber-600" />
              Active Alerts
              {alerts.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded-full">
                  {alerts.length}
                </span>
              )}
            </h3>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                  <p>No active alerts</p>
                </div>
              ) : (
                alerts.slice(0, 5).map((alert, index) => (
                  <AlertItem key={index} alert={alert} />
                ))
              )}
            </div>
            {alerts.length > 5 && (
              <button className="w-full mt-4 text-sm text-blue-600 font-medium hover:text-blue-700">
                View all {alerts.length} alerts
              </button>
            )}
          </div>
        </div>

        {/* Crew Distribution by Rank */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
            Crew Distribution by Rank
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {rankDistribution.map((item) => (
              <RankBar
                key={item.rank}
                {...item}
                maxValue={maxRankValue || 100}
              />
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center">
            <div className="flex gap-6">
              <span className="flex items-center text-sm text-gray-600">
                <span className="w-3 h-3 bg-blue-500 rounded-full mr-2" />
                Active on vessels
              </span>
              <span className="flex items-center text-sm text-gray-600">
                <span className="w-3 h-3 bg-emerald-500 rounded-full mr-2" />
                Available for assignment
              </span>
            </div>
            <button
              onClick={() => navigate('/analytics')}
              className="text-sm text-blue-600 font-medium hover:text-blue-700 flex items-center"
            >
              View Analytics <ArrowRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

